import classNames from "classnames";
import {
	ReactElement,
	ReadonlyRefObject,
	RefObject,
	useLayoutEffect,
	useMemo,
	useRef,
	useState
} from "react";
import styles from "./TruncatingList.module.less";
import {
	useResizeObserver,
	useSynchronizedRef
} from "client/Hooks";
import {
	clamp,
	isNil,
	isNotNil,
	setTrackingId
} from "client/Misc/utils";
import { calculateAmountOfListItemThatFitInTheContainer } from "client/Misc/domUtils";

export type TruncatingListProps<T> = {
	items: readonly T[];
	separator?: string;
	maxNumberOfVisibleItems?: number;
	hiddenCounter?: HiddenCounterOptions;
	trackingId: string;
	children: (item: T) => ReactElement;
} & ({
	wrappingBehavior: "ellipsis";
	shouldShowAtLeastOne?: boolean;
	onVisibleItemCountChange?: (count: number) => void;
} | {
	wrappingBehavior?: "wrap";
});

export const TruncatingList = <T,>(props: TruncatingListProps<T>): ReactElement => {
	const {
		items,
		wrappingBehavior = "wrap",
		separator = ",",
		maxNumberOfVisibleItems,
		trackingId,
		hiddenCounter,
		children
	} = props;

	const [hiddenItemsNumber, setHiddenItemsNumber] = useState<number>(0);

	const listRef: RefObject<HTMLUListElement | null> = useRef<HTMLUListElement>(null);
	const hiddenElementsCounterRef: RefObject<HTMLLIElement | null> = useRef<HTMLLIElement>(null);
	const { width, wasInitialized } = useResizeObserver(listRef);

	const shouldShowAtLeastOne = useMemo(
		(): boolean => {
			return props.wrappingBehavior === "ellipsis"
				? props.shouldShowAtLeastOne ?? false
				: true;
		},
		[props]
	);

	const onVisibleItemCountChangeRef: ReadonlyRefObject<((count: number) => void) | undefined> =
		useSynchronizedRef(
			props.wrappingBehavior === "ellipsis"
				? props.onVisibleItemCountChange
				: undefined
		);

	useLayoutEffect(
		(): void => {
			if (isNil(listRef.current) || !wasInitialized) {
				setHiddenItemsNumber(items.length);
				return;
			}

			if (wrappingBehavior !== "ellipsis") {
				setHiddenItemsNumber(0);
				return;
			}

			const itemThatFitInTheContainerCount: number = calculateAmountOfListItemThatFitInTheContainer(
				listRef.current,
				hiddenElementsCounterRef.current,
				width,
				items.length
			);

			const visibleItemsCount: number = clamp(
				itemThatFitInTheContainerCount,
				shouldShowAtLeastOne ? 1 : 0,
				maxNumberOfVisibleItems
			);

			setHiddenItemsNumber(
				Math.max(
					0,
					items.length - visibleItemsCount
				)
			);

			onVisibleItemCountChangeRef.current?.(
				visibleItemsCount
			);
		},
		[
			items.length,
			maxNumberOfVisibleItems,
			wasInitialized,
			width,
			wrappingBehavior,
			shouldShowAtLeastOne,
			onVisibleItemCountChangeRef
		]
	);

	return (
		<ul
			{...setTrackingId(trackingId)}
			className={
				classNames(
					styles.root,
					{
						[styles.root_wrap]: wrappingBehavior === "wrap"
					}
				)
			}
			ref={listRef}
		>
			{items.map((item, index) => {
				return (
					<li
						key={index}
						className={
							classNames(
								styles.listItem,
								{
									[styles.listItem_alwaysShown]: shouldShowAtLeastOne
										&& index === 0
										&& hiddenItemsNumber === items.length - 1,
									[styles.listItem_hidden]: wrappingBehavior === "ellipsis"
										&& (index >= items.length - hiddenItemsNumber),
									[styles.listItem_shrinkable]: shouldShowAtLeastOne
								}
							)
						}
					>
						<span
							{...setTrackingId("item-content")}
							className={styles.itemContent}>
							{children(item)}
						</span>
						{index < items.length - 1 && (
							<span className={styles.separator}>
								{separator}
							</span>
						)}
					</li>
				);
			})}
			<li
				{...setTrackingId("more")}
				ref={hiddenElementsCounterRef}
				className={
					classNames(
						styles.listItem,
						{
							[styles.listItem_hidden]: wrappingBehavior === "wrap"
								|| hiddenItemsNumber === 0
								|| hiddenCounter?.isHidden
						}
					)
				}
			>
				<span
					className={
						classNames(
							styles.hiddenItemsNumbers,
							{
								[styles.hiddenItemsNumbers_isBold]: hiddenCounter?.isBold,
								[styles.hiddenItemsNumbers_isClickable]: isNotNil(hiddenCounter?.onClick)
							}
						)
					}
					onClick={hiddenCounter?.onClick}
				>
					+{hiddenItemsNumber}
				</span>
			</li>
		</ul>
	);
};
TruncatingList.displayName="TruncatingList";

export type TruncatingListWrappingBehavior = "wrap" | "ellipsis";

export type HiddenCounterOptions = {
	isBold?: boolean;
	onClick?: () => void;
	isHidden?: boolean;
};
